import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { API_BASE_URL, authHeaders } from '../api/coworkApi';
import { getTokenFromStorage } from '@/utils/auth';

export interface Notification {
  id: string;
  team_id: string;
  agent_id: string;
  message: string;
  urgency: string;
  source: string;
  read: number;
  created_at: string;
  // From WebSocket broadcast
  agentName?: string;
  agentEmoji?: string;
}

const AGENT_LABELS: Record<string, { name: string; emoji: string }> = {
  'account-manager': { name: 'Lykke', emoji: '🤝' },
  'leo-perf': { name: 'Leo', emoji: '📊' },
  'maya-seo': { name: 'Maya', emoji: '🔍' },
  'kai-analytics': { name: 'Kai', emoji: '📈' },
  'nova-strategy': { name: 'Nova', emoji: '🧠' },
  'rex-influencer': { name: 'Rex', emoji: '👥' },
  'aria-content': { name: 'Aria', emoji: '✍️' },
  'jones-artdir': { name: 'Jones', emoji: '🎨' },
};

/** `enabled` false (a client, or /v1/me not answered yet): no fetch, no poll, no socket (staff endpoints). */
export function useNotifications(enabled = true) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/v1/notifications?limit=20`, {
        headers: authHeaders(false),
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread || 0);
    } catch {
      // silently fail
    }
  }, []);

  // Mark one as read
  const markRead = useCallback(async (id: string) => {
    try {
      await fetch(`${API_BASE_URL}/v1/notifications/${id}/read`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  }, []);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/v1/notifications/read-all`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  }, []);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!enabled) return;
    const token = getTokenFromStorage();
    if (!token) return;

    const wsUrl = API_BASE_URL.replace(/^http/, 'ws') + '/ws';
    let ws: WebSocket;
    let reconnectTimer: NodeJS.Timeout;
    let disposed = false;

    function connect() {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          // Authenticate with team ID
          ws.send(JSON.stringify({ type: 'auth', token }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'notification') {
              const agent = AGENT_LABELS[data.agentId] || { name: data.agentName || data.agentId, emoji: data.agentEmoji || '🤖' };

              // Show toast
              toast(data.message, {
                description: `${agent.emoji} ${agent.name}`,
                duration: data.urgency === 'important' ? 10000 : 6000,
              });

              // Update state
              setNotifications(prev => [data as Notification, ...prev].slice(0, 50));
              setUnreadCount(prev => prev + 1);
            }
          } catch { /* ignore parse errors */ }
        };

        ws.onclose = () => {
          if (disposed) return; // don't schedule a reconnect after cleanup
          // Reconnect after 30s
          reconnectTimer = setTimeout(connect, 30000);
        };
      } catch { /* ignore connection errors */ }
    }

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled]);

  // Initial fetch + poll every 2 minutes (skipped while the tab is hidden)
  useEffect(() => {
    if (!enabled) return;
    fetchNotifications();
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchNotifications();
    }, 120000);
    return () => clearInterval(interval);
  }, [fetchNotifications, enabled]);

  return { notifications, unreadCount, markRead, markAllRead, refresh: fetchNotifications };
}
